<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import Table from '../../ui/table/table.svelte';
  import TableHeader from '../../ui/table/table-header.svelte';
  import TableRow from '../../ui/table/table-row.svelte';
  import TableHead from '../../ui/table/table-head.svelte';
  import TableCell from '../../ui/table/table-cell.svelte';
  import TableBody from '../../ui/table/table-body.svelte';
  import { cartMockData, defaultHeaders } from './cart.mock';
  import { type CartItem, type CartData } from './cart.type';
  import { toast } from 'svelte-sonner';
  import Search from '$lib/components/custom/search/Search.svelte';
  import { Trash2 } from 'lucide-svelte';
  import Input from '$lib/components/ui/input/input.svelte';
  import Label from '$lib/components/ui/label/label.svelte';

  type User = { name: string; username: string; email?: string };

  let {
    open = $bindable(false),
    user = $bindable<any>(undefined),
    title = 'Cart',
    note = '',
    size = 'sm:max-w-[800px]',
    cart = $bindable<any>(cartMockData),
    experience = null as any,
  }: {
    open?: boolean;
    user?: any;
    title?: string;
    note?: string;
    size?: string;
    cart?: any;
    experience?: any;
  } = $props();

  let notes = $state('');

  let quotePartsOnly = $state(false);
  let quoteComplete = $state(false);

  let quoteType = $derived(
    [
      ...(quotePartsOnly ? ['parts_only'] : []),
      ...(quoteComplete ? ['complete_unit'] : []),
    ] as string[],
  );

  let loading = $state(false);

  // Listen for RFQ completion events
  $effect(() => {
    if (!experience?.eventHub) return;

    const handleRFQComplete = (result: { success: boolean; data?: any }) => {
      loading = false;
      // Close cart modal so the confirmation/error modal (shown by CartHandler) is visible
      open = false;
      // Success and error modals are shown by CartHandler.emitRFQComplete so they appear for all flows (e.g. after save prompt when cart was closed).
    };

    experience.eventHub.on('sendRFQ:complete', handleRFQComplete);

    return () => {
      experience.eventHub.off('sendRFQ:complete', handleRFQComplete);
    };
  });


  function isUserLoggedIn(): boolean {
    // Check if user is authenticated via LoginManager
    if (experience?.LoginManager?.authenticated) {
      return true;
    }
    
    // Check if user data exists in dataVault
    const dataVaultUser = experience?.dataVault?.user;
    if (dataVaultUser && dataVaultUser.id != null) {
      return true;
    }

    if (user && user.id != null && user.email) {
      return true;
    }
    
    return false;
  }

  function handleSendRFQ() {
    console.log(cart,' cart data')
    if (!experience?.eventHub) {
      // Close cart modal
      open = false;
      
      experience?.eventHub?.emit('modalInfo', {
        title: 'Error',
        message: 'Unable to send RFQ: experience not available',
        actions: [
          {
            label: 'OK',
            callback: () => {
              // Modal will close automatically, cart modal stays closed
            }
          }
        ],
        autoClose: false
      });
      return;
    }

    // Check if user is logged in
    if (!isUserLoggedIn()) {
      // Capture notes before closing — state is destroyed when open = false
      const capturedNotes = notes;
      open = false;

      experience.eventHub.emit('modalInfo', {
        title: 'Login Required',
        message: 'Please log in to send an RFQ. Or you can send an RFQ as a guest user.',
        actions: [
          {
            label: 'OK',
            callback: () => {}
          },
          // ! TODO: TEMPORARY REMOVED FOR LIVE ENV
          {
            label: 'Send RFQ as Guest',
            callback: () => {
              experience.eventHub.emit('openGuestRFQModal', { cart, notes: capturedNotes, quoteType });
            },
          },
        ],
        autoClose: false
      });
      return;
    }

    loading = true;

    // Emit sendRFQ event - CartHandler will handle the API call (designId from cart or current design)
    experience.eventHub.emit('sendRFQ', {
      cart,
      user,
      notes,
      designId: cart?.designId ?? experience?.projectUuid ?? (typeof window !== 'undefined' ? window.currentProjectUuid : undefined),
      quoteType,
    });
  }

  function handleDelete(id: CartItem['id']) {
    if (!cart?.data) return;

    const header = cart.header ?? defaultHeaders;
    const updatedData = cart.data.filter((row: CartItem) => row.id !== id);
    const newCartTotal = calculateCartTotal(updatedData, header);
    
    const updatedCart = {
      ...cart,
      data: updatedData,
      total: [
        cart.total?.[0] ?? cart.total?.[0] ?? '',
        Number.isFinite(newCartTotal) ? newCartTotal.toFixed(2) : '0.00',
      ],
    };
    
    // Update the local cart prop
    cart = updatedCart;
    
    // Notify CartHandler to update its internal cartData
    if (experience?.eventHub) {
      experience.eventHub.emit('cart:updated', updatedCart);
    }
  }

  let q = $state('');
  let filteredSearch = $derived.by<CartData[]>(() => {
    const term = q.trim().toLowerCase();
    const list = cart?.data ?? [];

    if (!term) return list;

    return list.filter((r: CartItem) => {
      const idMatch = r.id?.toString().includes(term);
      const nameMatch = r.partNumber?.toLowerCase().includes(term);

      return idMatch || nameMatch;
    });
  }) as any;

  const isQtyField = (field: string) =>
    String(field).toLowerCase().includes('qty');

  const isTotalField = (field: string) =>
    String(field).toLowerCase().includes('total');

  const isPriceField = (field: string) =>
    String(field).toLowerCase().includes('each') ||
    String(field).toLowerCase().includes('price');

  function changeQty(id: CartItem['id'], field: string, delta: number) {
    if (!cart?.data) return;

    const header = cart.header ?? defaultHeaders;

    const updatedData = cart.data.map((row: CartItem) => {
      if (row.id !== id) return row;

      const current = Number(row[field as keyof CartItem] ?? 0);
      const next = Math.max(1, current + delta);

      const updatedRow: CartItem = {
        ...row,
        [field]: next,
      } as CartItem;

      const fieldNames = Object.values(header);
      const totalField =
        fieldNames.find((f) => String(f).toLowerCase().includes('total')) ??
        'total';

      const newTotal = calculateRowTotal(updatedRow, header);

      return {
        ...updatedRow,
        [totalField as keyof CartItem]: newTotal,
      };
    });

    const newCartTotal = calculateCartTotal(updatedData, header);

    const updatedCart = {
      ...cart,
      data: updatedData,
      total: [
        cart.total?.[0] ?? cart.total?.[0] ?? '',
        Number.isFinite(newCartTotal) ? newCartTotal.toFixed(2) : '0.00',
      ],
    };

    // Update the local cart prop
    cart = updatedCart;
    
    // Notify CartHandler to update its internal cartData
    if (experience?.eventHub) {
      experience.eventHub.emit('cart:updated', updatedCart);
    }
  }

  function calculateRowTotal(item: CartItem, header: Record<string, string>) {
    const fieldNames = Object.values(header);

    const priceField = fieldNames.find((f) => f.toLowerCase().includes('each'));

    const qtyField = fieldNames.find((f) => f.toLowerCase().includes('qty'));

    if (!priceField || !qtyField) return 0;

    const qty = Number(item[qtyField as keyof CartItem] ?? 1);
    const price = Number(item[priceField as keyof CartItem] ?? 0);

    return qty * price;
  }

  function calculateCartTotal(
    data: CartItem[] = [],
    header: Record<string, string>,
  ) {
    return data.reduce((sum, item) => {
      return sum + calculateRowTotal(item, header);
    }, 0);
  }
</script>

<Dialog.Root bind:open preventScroll={false}>
  <Dialog.Content class={`${size} border-none shadow-lg !z-[1000001] !bg-white dark:!bg-[var(--theme-main)] dark:!text-[var(--theme-font)]`}>
    <Dialog.Header class="!p-4 !pb-0 !mt-4">
      <Dialog.Title class="text-xl font-semibold">{title}</Dialog.Title>
      {#if note}<Dialog.Description>{note}</Dialog.Description>{/if}
    </Dialog.Header>

    <div
      class="flex !flex-col gap-0 min-h-0 h-[min(560px,85vh)] max-h-[min(720px,90vh)]"
    >
      <div class="flex shrink-0 items-center w-full !px-4">
        <Search bind:q />
      </div>

      <div
        class="rounded-xl w-full bg-white dark:bg-[var(--theme-main)] mt-8 min-h-0 flex-1 overflow-auto"
      >
        <div class="">
          <Table class="text-xs min-w-full border-0">
            <TableHeader
              class="sticky top-0 z-10 !border-0 shadow-sm/0 !bg-[#f8fafc] dark:!bg-[var(--theme-canvas)] dark:!text-[var(--theme-font-alt)]"
            >
              <TableRow class="!border-0 ">
                {#if cart?.header}
                  {#each Object.entries(cart.header) as [label, field], i}
                    <TableHead class="!border-0 font-normal">{label}</TableHead>
                  {/each}
                {:else}
                  {#each Object.entries(defaultHeaders) as [label, field], i}
                    <TableHead class="!border-0 font-normal">{label}</TableHead>
                  {/each}
                {/if}
                <!-- TODO HARDCODED FOR NOW -->
                <TableHead class="!border-0 font-normal">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {#if filteredSearch.length}
                {#each filteredSearch as item, i (item.id)}
                  <TableRow
                    class="hover:bg-slate-50 dark:hover:bg-[var(--theme-canvas)] !border-[#E5E7EB] 
    hover:!border-[#D1D5DB]
"
                  >
                    {#each Object.values(cart.header) as field}
                      <TableCell class="text-center !px-2 !py-3">
                        {#if isQtyField(field as any)}
                          <div
                            class="inline-flex items-center justify-center gap-1"
                          >
                            <button
                              type="button"
                              class="h-6 w-6 flex items-center justify-center rounded-full border border-slate-300 dark:border-gray-500 text-xs hover:bg-slate-100 dark:hover:bg-[var(--theme-canvas)] disabled:opacity-40 disabled:cursor-not-allowed"
                              onclick={() =>
                                changeQty(item.id, field as any, -1)}
                              disabled={Number(
                                item[field as keyof typeof item] ?? 0,
                              ) <= 0}
                              aria-label="Decrease quantity"
                            >
                              -
                            </button>

                            <span class="min-w-[2rem] inline-block text-center">
                              {item[field as keyof typeof item]}
                            </span>

                            <button
                              type="button"
                              class="h-6 w-6 flex items-center justify-center rounded-full border border-slate-300 dark:border-gray-500 text-xs hover:bg-slate-100 dark:hover:bg-[var(--theme-canvas)]"
                              onclick={() =>
                                changeQty(item.id, field as any, 1)}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        {:else if isTotalField(field as any)}
                          ${calculateRowTotal(item, cart?.header).toFixed(2)}
                        {:else if isPriceField(field as any)}
                          ${Number(item[field as keyof typeof item] ?? 0).toFixed(2)}
                        {:else}
                          {item[field as keyof typeof item]}
                        {/if}
                      </TableCell>
                    {/each}

                    <!-- TODO HARDCODED FOR NOW -->
                    <TableCell class={'text-center font-medium !px-1 !py-2'}>
                      <button
                        type="button"
                        class="inline-flex items-center justify-center rounded-full
           text-slate-400 dark:text-[var(--theme-font)] hover:!text-slate-700 dark:hover:!text-[var(--theme-font-alt)] hover:!bg-slate-300 dark:hover:!bg-[var(--theme-canvas)]
           transition"
                        onclick={() => handleDelete(item.id)}
                        aria-label="Remove item from cart"
                      >
                        <Trash2 class="w-4 h-4 " color="#2E2E2E" />
                      </button>
                    </TableCell>
                  </TableRow>
                {/each}
              {:else}
                <TableRow>
                  <TableCell
                    colspan={Object.values(cart?.header ?? defaultHeaders)
                      .length + 1}
                    class="!py-8 text-center text-muted-foreground"
                  >
                    No items found.
                  </TableCell>
                </TableRow>
              {/if}
            </TableBody>
          </Table>
        </div>
      </div>

      <div class="w-full flex shrink-0 !items-center gap-4 !justify-end !px-6">
        <p class="text-sm font-semibold">Total:</p>
        <p class="text-sm font-semibold">
          {#if cart?.data && cart?.data.length > 0}
            ${cart.total[1]}
          {/if}
        </p>
      </div>

    

      <div class="w-full flex shrink-0 !flex-col !items-start gap-0 !px-4">
        <Label for="notes" class="text-left text-xs font-normal">Notes</Label>
        <Input
          id="notes"
          bind:value={notes}
          class="col-span-3 !w-full !px-4 !py-5 text-xs !outline-none
                  focus:!outline-none
                  focus-visible:!outline-none
                  
                  focus:ring-0
                  focus-visible:ring-0
                  !ring-0
                  !shadow-none
                  focus:!shadow-none
                  focus-visible:!shadow-none"
          placeholder="Leave your notes here..."
        />
      </div>

      <div class="w-full flex shrink-0 !flex-col !items-start gap-2 rounded-lg border border-slate-200 dark:border-[var(--theme-canvas)] bg-slate-50 dark:bg-[var(--theme-canvas)] !px-6 !py-3 !mt-4">
        <p class="text-xs font-medium">What would you like us to quote?</p>

        <div class="flex !items-start !gap-3">
          <input
            id="quote-parts-only-checkbox"
            type="checkbox"
            bind:checked={quotePartsOnly}
            class="mt-0.5 h-4 w-4 shrink-0 accent-[#004976] dark:accent-[var(--theme-link)] cursor-pointer"
          />
          <label for="quote-parts-only-checkbox" class="text-xs cursor-pointer select-none">
            Quote parts only per the BOM.
          </label>
        </div>

        <div class="flex !items-start !gap-3">
          <input
            id="quote-complete-checkbox"
            type="checkbox"
            bind:checked={quoteComplete}
            class="mt-0.5 h-4 w-4 shrink-0 accent-[#004976] dark:accent-[var(--theme-link)] cursor-pointer"
          />
          <label for="quote-complete-checkbox" class="text-xs cursor-pointer select-none">
            Quote a complete assembled and tested unit based on the XMD model.
          </label>
        </div>

        {#if quoteComplete}
          <p class="text-[11px] text-muted-foreground leading-relaxed">
            By checking this, you agree to share your design with X-Microwave.
          </p>
        {/if}

        {#if quoteType.length === 0}
          <p class="text-[11px] text-red-500 leading-relaxed">
            Please select at least one option.
          </p>
        {/if}
      </div>
    </div>

    <Dialog.Footer class="!p-2">
      <Button
        variant="outline"
        onclick={() => (open = false)}
        class={` h-9 !px-4 !py-6 border-2 border-[#DADADA] !shadow-none  !text-sm font-normal hover:brightness-110 transition`}
        >Cancel</Button
      >
      <Button
        disabled={loading || cart?.data?.length === 0 || quoteType.length === 0}
        onclick={handleSendRFQ}
        class={` h-9 !px-4 !py-6 border-none !shadow-none  !text-sm font-normal hover:brightness-110 transition text-white !bg-[#004976] dark:!bg-[var(--theme-link)]`}>Send RFQ</Button
      >
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
